import {
  FC,
  forwardRef,
  ReactElement,
  Ref,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Slide,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { TransitionProps } from "@mui/material/transitions";
import { Edit, Sports, Sync, Videocam } from "@mui/icons-material";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { SamsScope, samsScopes } from "@/models/samsVbl";
import { PdfData } from "@/models/pdfData";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { loadGameData, tokenNeeded } from "@/services/samsService";
import { millisToDateTime } from "@/services/dateTimeService";
import { Views } from "@/models/gameGeneral";
import { useGameDataContext } from "@context/GameDataContext";
import GameSelectionDropdown, {
  GameSelectionDropdownOption,
} from "@components/controls/GameSelectionDropdown";
import DateTimePicker from "@components/DateTimePicker";
import GameSelectionTreeView, {
  LeagueTree,
} from "@/components/controls/GameSelectionTreeView";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...{ ...props, key: undefined }} />;
});

const teamAvatarSx = {
  height: "10vw",
  width: "10vw",
  maxWidth: "150px",
  maxHeight: "150px",
};

const CreateGameWizard: FC<{
  open: boolean;
  handleClose: () => void;
  onCreateGame: (
    game: Pick<PdfData, "scope" | "samsId">,
    destination: Views,
  ) => void;
}> = ({ open, handleClose, onCreateGame }) => {
  const [searchParams] = useSearchParams();
  const { onMobile, isTouchScreen } = useResponsiveContext();

  const [samsId, setSamsId] = useState("");
  const [selectedGame, setSelectedGame] =
    useState<GameSelectionDropdownOption>();

  const [authToken, setAuthToken] = useState<string>();

  const [loadingGames, setLoadingGames] = useState(true);
  const [gameOptions, setGameOptions] = useState<GameSelectionDropdownOption[]>(
    [],
  );
  const [games, setGames] = useState<{ [key in SamsScope]?: PdfData[] }>({});
  const [scope, setScope] = useState<SamsScope>(
    samsScopes.find((s) => searchParams.get("scope") === s) || "vbl",
  );

  const [inputMode, setInputMode] = useState<"select" | "search">("search");

  useEffect(() => {
    setInputMode(scope === "vbl" && isTouchScreen ? "select" : "search");
  }, [isTouchScreen]);

  const selectInputScopes: SamsScope[] = ["vbl"];

  const switchVblView = () => {
    setInputMode((prev) => (prev === "select" ? "search" : "select"));
  };

  const inputModeIs = (inputModeToTest: "select" | "search") => {
    if (selectInputScopes.includes(scope)) {
      return inputMode === inputModeToTest;
    } else {
      return inputModeToTest === "search";
    }
  };

  const updateGameOptions = async (scope: SamsScope) => {
    setLoadingGames(true);

    const downloadGameData = (scope: SamsScope) =>
      loadGameData({ scope }).then((data) => {
        setGames((prevGames) => ({ ...prevGames, [scope]: data }));
        return data;
      });

    const currentDateTime = Date.now() - 30 * 60 * 1000;

    const gameData: GameSelectionDropdownOption[] = (
      games[scope] || (await downloadGameData(scope))
    )
      .sort((a, b) => {
        if (
          a.gameData.gameStartDateTime < currentDateTime &&
          b.gameData.gameStartDateTime >= currentDateTime
        ) {
          return 1;
        } else if (
          b.gameData.gameStartDateTime < currentDateTime &&
          a.gameData.gameStartDateTime >= currentDateTime
        ) {
          return -1;
        } else {
          return a.gameData.gameStartDateTime - b.gameData.gameStartDateTime;
        }
      })
      .map((game) => ({
        ...game,
        prettyDate: millisToDateTime(game.gameData.gameStartDateTime),
      }));

    setGameOptions(gameData);

    setLoadingGames(false);
  };

  useEffect(() => {
    if (scope) {
      updateGameOptions(scope);
    }
  }, [scope]);

  useEffect(() => {
    setSelectedGame(gameOptions.find((game) => game.samsId === samsId));
  }, [samsId, gameOptions]);

  const handleSubmit = (destination: Views) => {
    if (selectedGame) {
      onCreateGame(selectedGame, destination);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      fullWidth={true}
      fullScreen={onMobile}
      maxWidth={"sm"}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>
        {`${!selectedGame ? "Spiel auswählen" : "Modus auswählen"}`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description"></DialogContentText>
        <Grid container spacing={2} columns={12}>
          {!selectedGame ? (
            <>
              <Grid size={12}>
                <ToggleButtonGroup
                  color={"primary"}
                  exclusive
                  onChange={(e, value) => value && setScope(value)}
                  value={scope}
                  fullWidth
                >
                  <ToggleButton value="vbl">VBL</ToggleButton>
                  <ToggleButton value="dvv">DVV</ToggleButton>
                  <ToggleButton value="bvv">BVV</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid
                size={12}
                container
                minHeight={"290px"}
                alignItems={"center"}
                justifyContent={"stretch"}
                display={"flex"}
              >
                {inputModeIs("search") && (
                  <Grid size={12} flex={1} alignSelf={"start"}>
                    <GameSelectionDropdown
                      loadingGames={loadingGames}
                      games={gameOptions}
                      onGameSelected={(samsId: string) => setSamsId(samsId)}
                    />
                  </Grid>
                )}
                {inputModeIs("select") && (
                  <Grid size={12} container>
                    <GameSelectionTreeView
                      loadingGames={loadingGames}
                      games={gameOptions}
                      onGameSelected={(samsId: string) => setSamsId(samsId)}
                    />
                  </Grid>
                )}
              </Grid>

              {tokenNeeded(scope) && (
                <Grid size={12} alignContent={"start"}>
                  <TextField
                    autoFocus
                    required
                    margin="dense"
                    id={`authToken`}
                    label="Token"
                    type="text"
                    fullWidth
                    value={authToken}
                    onChange={(event) => setAuthToken(event.target.value)}
                    variant="outlined"
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              <Grid size={4} alignItems={"center"} justifyItems={"center"}>
                <Avatar
                  alt={selectedGame?.gameData.team1.shortName}
                  src={selectedGame?.gameData.team1.iconUrl}
                  sx={teamAvatarSx}
                />
              </Grid>
              <Grid size={4} alignContent={"center"} justifyItems={"center"}>
                {millisToDateTime(
                  selectedGame?.gameData.gameStartDateTime,
                  "long",
                )}
              </Grid>
              <Grid size={4} alignItems={"center"} justifyItems={"center"}>
                <Avatar
                  alt={selectedGame?.gameData.team2.shortName}
                  src={selectedGame?.gameData.team2.iconUrl}
                  sx={teamAvatarSx}
                />
              </Grid>
              <Grid size={12}>
                <Divider variant={"middle"} />
              </Grid>
              <Grid size={6}>
                <Button
                  variant={"outlined"}
                  fullWidth
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    py: 2,
                  }}
                  onClick={() => handleSubmit("referee")}
                >
                  <Sports fontSize={"large"} />
                  <Typography>Schiri-Tablet</Typography>
                </Button>
              </Grid>
              <Grid size={6}>
                <Button
                  variant={"outlined"}
                  fullWidth
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    py: 2,
                  }}
                  onClick={() => handleSubmit("challenge")}
                >
                  <Videocam fontSize={"large"} />
                  <Typography>Challenge</Typography>
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap" }}>
        <DialogActions sx={{ marginRight: "auto" }}>
          {!selectedGame && scope === "vbl" && (
            <Button onClick={switchVblView}>
              {inputMode === "select" ? "Spielsuche" : "Spielauswahl"}
            </Button>
          )}
          {selectedGame && (
            <Button onClick={() => setSamsId("")}>{"< Zurück"}</Button>
          )}
        </DialogActions>
        <DialogActions sx={{ marginLeft: "auto" }}>
          <Button onClick={handleClose}>Abbrechen</Button>
        </DialogActions>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGameWizard;
